// Duenner Client fuer das AB-Erfassungs-Backend unter /api/ab (Muster: modules/einvoicing/api.js).
// MONOLITH-KONVENTION: `user` ist das ERSTE Argument jeder Funktion und traegt den JWT
// (.token, mock-Token wird uebersprungen). apiCall aus core/api/apiService.js setzt IMMER
// Content-Type: application/json und kann daher keine FormData-Uploads senden — Upload und
// PDF-Vorschau nutzen deshalb (wie im E-Invoicing-Modul) einen eigenen fetch mit Bearer-Header.

const abCall = async (endpoint, method = 'GET', data = null, user = null) => {
  const config = { method, headers: {} };
  if (user?.token && !user.token.startsWith('mock-')) {
    config.headers['Authorization'] = `Bearer ${user.token}`;
  }
  if (data instanceof FormData) {
    config.body = data; // Browser setzt multipart-Boundary selbst
  } else if (data !== null && data !== undefined) {
    config.headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify(data);
  }
  const response = await fetch(`/api/ab${endpoint}`, config);
  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch { /* kein JSON-Body */ }
    const err = new Error(body?.message || `HTTP ${response.status}`);
    err.status = response.status;
    // Vollstaendigen Fehler-Body mitreichen (z. B. { message, groups } beim SAP-Commit-409),
    // damit Aufrufer strukturierte Fehlerdetails auswerten koennen, ohne die Fehler-Shape zu aendern.
    err.body = body;
    throw err;
  }
  if (response.status === 204) return null;
  return response.json();
};

// ==================== DOKUMENTE ====================

export const listDocuments = (user, params = {}) => {
  // 'supplier' ist der EINZIGE Parameter, bei dem der Leerstring fachlich etwas bedeutet
  // ("ohne Lieferant", Backend-Kontrakt supplier='') — er wird deshalb auch leer mitgesendet.
  const q = new URLSearchParams(
    Object.entries(params).filter(([k, v]) => v !== undefined && v !== null && (v !== '' || k === 'supplier'))
      .map(([k, v]) => [k, String(v)])
  );
  const qs = q.toString();
  return abCall(`/documents${qs ? `?${qs}` : ''}`, 'GET', null, user);
};

export const getStats = (user) => abCall('/documents/stats', 'GET', null, user);

export const getDocument = (user, id) => abCall(`/documents/${id}`, 'GET', null, user);

export const getCompare = (user, id) => abCall(`/documents/${id}/compare`, 'GET', null, user);

/** Upload: multipart Feld "file" (nur PDF, max 20 MB serverseitig geprueft). */
export const uploadDocument = (user, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return abCall('/documents', 'POST', fd, user);
};

/**
 * Blob-URL des Original-PDFs fuer die Inline-Vorschau (iframe) — der iframe kann den
 * JWT-Header nicht selbst mitschicken, daher laden wir per fetch+Bearer und erzeugen
 * eine Objekt-URL. Aufrufer MUSS die URL nach Gebrauch mit URL.revokeObjectURL freigeben.
 */
export const pdfBlobUrl = async (user, id) => {
  const config = { headers: {} };
  if (user?.token && !user.token.startsWith('mock-')) config.headers['Authorization'] = `Bearer ${user.token}`;
  const res = await fetch(`/api/ab/documents/${id}/pdf`, config);
  if (!res.ok) throw new Error(`PDF laden fehlgeschlagen (HTTP ${res.status})`);
  return URL.createObjectURL(await res.blob());
};

export const patchLineField = (user, id, lineNo, field, value) =>
  abCall(`/documents/${id}/lines/${lineNo}/fields/${field}`, 'PATCH', { value }, user);

export const addLine = (user, id, payload) => abCall(`/documents/${id}/lines`, 'POST', payload, user);

export const deleteLine = (user, id, lineNo) => abCall(`/documents/${id}/lines/${lineNo}`, 'DELETE', null, user);

export const reprocessDocument = (user, id) => abCall(`/documents/${id}/reprocess`, 'POST', {}, user);

export const approveDocument = (user, id) => abCall(`/documents/${id}/approve`, 'POST', {}, user);

export const rejectDocument = (user, id, reason) => abCall(`/documents/${id}/reject`, 'POST', { reason }, user);

export const deleteDocument = (user, id) => abCall(`/documents/${id}`, 'DELETE', null, user);

// ==================== STAMMDATEN ====================

export const listRulesets = (user) => abCall('/rulesets', 'GET', null, user);

export const getOrder = (user, ebeln) => abCall(`/orders/${encodeURIComponent(ebeln)}`, 'GET', null, user);

export const getHealth = (user) => abCall('/health', 'GET', null, user);

// ==================== REGELSET-VERWALTUNG (admin) ====================
// Kontrakt: backend/modules/auftragsbestaetigung/routesAdmin.js (Agent MAIL). Mutationen
// serverseitig ADMIN-geschuetzt; Vorschau ist lesend und fuer jeden authentifizierten Nutzer offen.

export const getRuleset = (user, id) => abCall(`/rulesets/${id}`, 'GET', null, user);

/** Legt eine neue (inaktive) Version an — version = max+1 je supplierId (serverseitig). */
export const createRuleset = (user, { supplierId, supplierName, rulesJson }) =>
  abCall('/rulesets', 'POST', { supplierId, supplierName, rulesJson }, user);

export const activateRuleset = (user, id) => abCall(`/rulesets/${id}/activate`, 'POST', {}, user);

export const deactivateRuleset = (user, id) => abCall(`/rulesets/${id}/deactivate`, 'POST', {}, user);

/** Nur inaktive Versionen loeschbar — Backend antwortet sonst mit 409. */
export const deleteRuleset = (user, id) => abCall(`/rulesets/${id}`, 'DELETE', null, user);

/** Testet ein (ggf. noch ungespeichertes) Regelset gegen ein bestehendes Dokument, ohne zu persistieren. */
export const previewRuleset = (user, { rulesJson, documentId }) =>
  abCall('/rulesets/preview', 'POST', { rulesJson, documentId }, user);

// ==================== KI-REGELSET-VORSCHLAG (Job-Polling) ====================

export const startSuggest = (user, documentId) =>
  abCall(`/documents/${documentId}/suggest-ruleset`, 'POST', {}, user);

export const listSuggestJobs = (user) => abCall('/suggest-jobs', 'GET', null, user);

export const getSuggestJob = (user, jobId) => abCall(`/suggest-jobs/${jobId}`, 'GET', null, user);

/** Legt aus dem Job-Ergebnis einen Regelset-Draft an (is_active=0) — ADMIN. */
export const applySuggest = (user, jobId) => abCall(`/suggest-jobs/${jobId}/apply`, 'POST', {}, user);

// ==================== MAIL-EINGANG (admin) ====================

export const mailStatus = (user) => abCall('/mail/status', 'GET', null, user);

/** Manueller Sync-Lauf (single-flight serverseitig) — 409 wenn bereits laeuft oder deaktiviert. */
export const mailSync = (user) => abCall('/mail/sync', 'POST', {}, user);

const api = {
  listDocuments, getStats, getDocument, getCompare, uploadDocument, pdfBlobUrl,
  patchLineField, addLine, deleteLine, reprocessDocument, approveDocument, rejectDocument,
  deleteDocument, listRulesets, getOrder, getHealth,
  getRuleset, createRuleset, activateRuleset, deactivateRuleset, deleteRuleset, previewRuleset,
  startSuggest, listSuggestJobs, getSuggestJob, applySuggest,
  mailStatus, mailSync,
};
export default api;

// ==================== UI-PARITAET (Welle 3): Dashboard / Monitoring / Posteingang / Report / PDF ====================
// Kontrakt: BAUPLAN-AB-UI-PARITAET (BE-1 = routes.js/routesMonitoring.js, BE-2 = routesAdmin.js).
// Diese Funktionen sind VORAB angelegt (Orchestrator) — FE-Agenten nur konsumieren, nicht aendern.

/** Dashboard-Aggregat: { kpis, trend[7], topSuppliers, today }. */
export const getDashboard = (user) => abCall('/dashboard', 'GET', null, user);

/** Lieferanten-Facette fuer den Dokumentfilter: [{ supplierId, supplierName, count }]. */
export const listDocumentSuppliers = (user) => abCall('/documents/suppliers', 'GET', null, user);

/** OCR-Wortboxen (Cache mit Live-Fallback): { pages: [{ page, width, height, words: [{x0,y0,x1,y1,t}] }] }. */
export const getWords = (user, id) => abCall(`/documents/${id}/words`, 'GET', null, user);

/** Abgleich mit optionaler HTN/AMPL-Pruefung (?ampl=true ergaenzt mfrpnState/validMfrpns je Zeile). */
export const getCompareAmpl = (user, id) => abCall(`/documents/${id}/compare?ampl=true`, 'GET', null, user);

// ---- Melden / Report-Workflow ----
export const reportDocument = (user, id, comment) => abCall(`/documents/${id}/report`, 'POST', { comment }, user);
export const resolveReport = (user, id, comment) => abCall(`/documents/${id}/report/resolve`, 'POST', { comment }, user);
export const getReportComments = (user, id) => abCall(`/documents/${id}/report/comments`, 'GET', null, user);
export const listMyReports = (user) => abCall('/reports/mine', 'GET', null, user);

// ---- Posteingang (Mail) ----
/** tab: 'open' | 'done'. Response { items, total, page, pageSize }. */
export const listMailInbox = (user, { tab = 'open', q = '', page = 1, pageSize = 20 } = {}) => {
  const qs = new URLSearchParams({ tab, page: String(page), pageSize: String(pageSize) });
  if (q) qs.set('q', q);
  return abCall(`/mail/inbox?${qs.toString()}`, 'GET', null, user);
};
/** Mail-Detail inkl. bodyText + verknuepfte Dokumente. */
export const getMailMessage = (user, id) => abCall(`/mail/messages/${id}`, 'GET', null, user);

// ---- Monitoring (admin) ----
/** kind: 'mail' | 'extraction' | 'llm' | 'actions' | 'reported'. */
export const getMonitoring = (user, kind, { page = 1, pageSize = 50 } = {}) =>
  abCall(`/monitoring/${kind}?page=${page}&pageSize=${pageSize}`, 'GET', null, user);
